import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatRM, type PaymentMethod, type PublicUser, type SaleDetail, type SaleSummary } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { useToast } from '../../context/ToastContext.js';
import { computeDateRangePreset, type DateRangePreset } from '../../lib/dateRange.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { BigButton } from '../../components/BigButton.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Page, PageHeader, Field } from '../../components/layout.js';

const PAYMENT_LABELS: Record<string, string> = { cash: 'Cash', qr: 'QR', card: 'Card', other: 'Other' };

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(iso));
}

export function SalesPage() {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialRange = computeDateRangePreset('today');
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [userId, setUserId] = useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [users, setUsers] = useState<PublicUser[]>([]);

  const [sales, setSales] = useState<SaleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSale, setSelectedSale] = useState<SaleDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pendingVoidId, setPendingVoidId] = useState<number | null>(null);
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    void api
      .listUsers()
      .then((r) => setUsers(r.users))
      .catch(() => {});
  }, []);

  const load = useCallback(async (f: string, t: string, uid: number | '', method: PaymentMethod | '') => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listSales({
        from: f,
        to: t,
        userId: uid === '' ? undefined : uid,
        paymentMethod: method === '' ? undefined : method,
        limit: 200,
      });
      setSales(res.sales);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load sales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(from, to, userId, paymentMethod);
  }, [from, to, userId, paymentMethod, load]);

  const openDetail = useCallback(
    async (saleId: number) => {
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        setSelectedSale(await api.getSale(saleId));
      } catch (err) {
        showToast(err instanceof ApiError ? err.message : 'Could not load sale details', 'error');
        setDetailOpen(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    const saleIdParam = searchParams.get('saleId');
    if (saleIdParam) {
      const id = Number(saleIdParam);
      if (Number.isInteger(id) && id > 0) void openDetail(id);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('saleId');
        return next;
      });
    }
    // Only ever react to the initial saleId param, not to every params change.
  }, []);

  function closeDetail() {
    setDetailOpen(false);
    setSelectedSale(null);
  }

  function applyPreset(preset: DateRangePreset) {
    const range = computeDateRangePreset(preset);
    setFrom(range.from);
    setTo(range.to);
  }

  async function confirmVoid() {
    if (pendingVoidId === null) return;
    setVoiding(true);
    try {
      await api.voidSale(pendingVoidId);
      showToast('Sale voided', 'success');
      setPendingVoidId(null);
      closeDetail();
      await load(from, to, userId, paymentMethod);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not void this sale', 'error');
      setPendingVoidId(null);
    } finally {
      setVoiding(false);
    }
  }

  // The server now applies the staff and payment-method filters (see
  // `load` above), so `sales` already matches what's requested - no need
  // to re-filter client-side, and the CSV export (built from the same
  // three params below) always matches what's on screen. See QA report P2 #4.
  const visibleSales = sales;
  const totals = visibleSales.reduce(
    (acc, s) => {
      if (s.voidedAt) return acc;
      acc.subtotal += s.subtotalCents;
      acc.discount += s.discountCents;
      acc.total += s.totalCents;
      return acc;
    },
    { subtotal: 0, discount: 0, total: 0 },
  );

  return (
    <Page>
      <PageHeader
        title="Sales"
        actions={
          <a
            href={`/api/export/sales.csv?from=${from}&to=${to}${userId !== '' ? `&userId=${userId}` : ''}${
              paymentMethod !== '' ? `&paymentMethod=${paymentMethod}` : ''
            }`}
          >
            <BigButton variant="outline">Export CSV</BigButton>
          </a>
        }
      />

      <div className="toolbar">
        <div className="chip-row basis-full">
          <PresetChip label="Today" onClick={() => applyPreset('today')} />
          <PresetChip label="Yesterday" onClick={() => applyPreset('yesterday')} />
          <PresetChip label="Last 7 days" onClick={() => applyPreset('last7')} />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:flex sm:gap-3">
          <Field label="From" htmlFor="sales-from">
            <input id="sales-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input" />
          </Field>
          <Field label="To" htmlFor="sales-to">
            <input id="sales-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
          </Field>
        </div>
        <Field label="Staff" htmlFor="sales-staff">
          <select
            id="sales-staff"
            value={userId}
            onChange={(e) => setUserId(e.target.value === '' ? '' : Number(e.target.value))}
            className="input sm:w-56"
          >
            <option value="">All staff</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Payment" htmlFor="sales-payment">
          <select
            id="sales-payment"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod | '')}
            className="input sm:w-56"
          >
            <option value="">All payment methods</option>
            <option value="cash">Cash</option>
            <option value="qr">QR</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
        </Field>
      </div>

      {loading && sales.length === 0 ? (
        <Spinner label="Loading sales" />
      ) : error ? (
        <EmptyState title="Could not load sales" description={error} />
      ) : visibleSales.length === 0 ? (
        <EmptyState title="No sales in this range" description="Try a different date range or filter." />
      ) : (
        <>
          <ul className="list-stack">
            {visibleSales.map((sale) => (
              <li key={sale.id}>
                <button
                  type="button"
                  onClick={() => void openDetail(sale.id)}
                  className="card-row w-full text-left flex items-center justify-between gap-3"
                >
                  <div>
                    <p className="font-body font-semibold text-base text-text">
                      #{sale.id} &middot; {formatDateTime(sale.createdAt)}
                    </p>
                    <p className="font-body text-sm text-muted">
                      {sale.soldByName} <span className="font-semibold">({sale.soldByCode})</span> &middot;{' '}
                      {PAYMENT_LABELS[sale.paymentMethod] ?? sale.paymentMethod} &middot; {sale.itemCount} item
                      {sale.itemCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {sale.voidedAt && (
                      <span className="rounded-full bg-orange text-ink font-heading font-bold text-sm px-2 py-1">
                        VOIDED
                      </span>
                    )}
                    <span className="font-heading font-semibold text-lg text-ink">{formatRM(sale.totalCents)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>

          <dl className="card flex flex-col gap-1">
            <div className="flex justify-between font-body text-base text-text">
              <dt>Subtotal</dt>
              <dd>{formatRM(totals.subtotal)}</dd>
            </div>
            <div className="flex justify-between font-body text-base text-text">
              <dt>Discount</dt>
              <dd>&minus;{formatRM(totals.discount)}</dd>
            </div>
            <div className="flex justify-between font-heading font-semibold text-xl text-ink pt-1">
              <dt>Total (excl. voided)</dt>
              <dd>{formatRM(totals.total)}</dd>
            </div>
          </dl>
        </>
      )}

      {detailOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 sm:p-6"
          role="presentation"
          onClick={closeDetail}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-sale-detail-title"
            className="w-full max-w-lg bg-surface rounded-t-2xl sm:rounded-2xl shadow-xl p-4 sm:p-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading || !selectedSale ? (
              <Spinner label="Loading sale" />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h2 id="admin-sale-detail-title" className="font-heading font-semibold text-xl text-ink">
                    Sale #{selectedSale.id}
                  </h2>
                  <button
                    type="button"
                    onClick={closeDetail}
                    aria-label="Close sale details"
                    className="min-h-12 min-w-12 rounded-full text-muted"
                  >
                    &times;
                  </button>
                </div>

                <p className="font-body text-sm text-muted">
                  Sold by {selectedSale.soldByName} <span className="font-semibold">({selectedSale.soldByCode})</span>{' '}
                  &middot; {PAYMENT_LABELS[selectedSale.paymentMethod]}
                </p>

                {selectedSale.voidedAt && (
                  <div className="flex flex-col gap-1">
                    <span className="self-start rounded-full bg-orange text-ink font-heading font-bold text-sm px-2 py-1">
                      VOIDED
                    </span>
                    {selectedSale.voidedByName && (
                      <p className="font-body text-sm text-muted">
                        Voided by {selectedSale.voidedByName}{' '}
                        <span className="font-semibold">({selectedSale.voidedByCode})</span>
                      </p>
                    )}
                  </div>
                )}

                <ul className="flex flex-col gap-2">
                  {selectedSale.items.map((item) => (
                    <li key={item.id} className="flex justify-between font-body text-base text-text">
                      <span>
                        {item.qty} &times; {item.productName} ({item.code}) @ {formatRM(item.unitPriceCents)}
                      </span>
                      <span>{formatRM(item.lineTotalCents)}</span>
                    </li>
                  ))}
                </ul>

                {selectedSale.note && <p className="font-body text-sm text-muted">Note: {selectedSale.note}</p>}

                <dl className="flex flex-col gap-1 pt-2 border-t border-border">
                  <div className="flex justify-between font-body text-base text-text">
                    <dt>Subtotal</dt>
                    <dd>{formatRM(selectedSale.subtotalCents)}</dd>
                  </div>
                  {selectedSale.autoDiscountCents > 0 && (
                    <div className="flex justify-between font-body text-base text-text">
                      <dt>Automatic discount{selectedSale.autoDiscountLabel ? ` (${selectedSale.autoDiscountLabel})` : ''}</dt>
                      <dd>&minus;{formatRM(selectedSale.autoDiscountCents)}</dd>
                    </div>
                  )}
                  {selectedSale.discountCents > 0 && (
                    <div className="flex justify-between font-body text-base text-text">
                      <dt>Discount</dt>
                      <dd>&minus;{formatRM(selectedSale.discountCents)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between font-heading font-semibold text-xl text-ink">
                    <dt>Total</dt>
                    <dd>{formatRM(selectedSale.totalCents)}</dd>
                  </div>
                </dl>

                {!selectedSale.voidedAt && (
                  <BigButton variant="danger" onClick={() => setPendingVoidId(selectedSale.id)}>
                    Void sale
                  </BigButton>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingVoidId !== null}
        title="Void this sale?"
        description="Stock for its items will be restored. This cannot be undone."
        confirmLabel={voiding ? 'Voiding…' : 'Yes, void sale'}
        variant="danger"
        onConfirm={() => void confirmVoid()}
        onCancel={() => setPendingVoidId(null)}
      />
    </Page>
  );
}

function PresetChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-12 px-4 rounded-full border-2 border-border bg-surface font-body font-semibold text-sm text-teal-dark"
    >
      {label}
    </button>
  );
}
